import { useState, useEffect, useMemo, useRef, useCallback, useLayoutEffect, useDeferredValue } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Plus, X, Trash2, PanelLeftClose, PanelLeftOpen, Save } from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import Prism from "prismjs"
import "prismjs/components/prism-sql"
import { useListResources, useListContainers, useListContainerFields } from "@/hooks/useResources"
import { useUpdateWidget } from "@/hooks/useBoard"
import {
  useDeleteQuery,
  useGenerateQuery,
  useListSavedQueries,
  useRunQuery,
  useSaveQuery,
  useUpdateQuery,
} from "@/hooks/useEditor"
import { useToast } from "@/hooks/useToast"
import { parseApiError } from "@/lib/http/client"
import { saveQuery as saveQueryHttp } from "@/lib/http/editor"
import { ROUTES } from "@/routes/path"
import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels"
import { ExportToBoardDialog } from "./components/export-to-board-dialog"
import { SavedQueriesSidebar } from "./components/saved-queries-sidebar"
import { SqlEditorPanel } from "./components/sql-editor-panel"
import { QueryResultsPanel } from "./components/query-results-panel"
import type { SavedQueryItem } from "@/lib/http/editor"

function getSqlHighlightRects(
  textarea: HTMLTextAreaElement,
  scrollEl: HTMLElement,
  start: number,
  end: number
): { left: number; top: number; width: number; height: number }[] {
  const value = textarea.value ?? ""
  const lh = parseFloat(window.getComputedStyle(textarea).lineHeight || "18") || 18
  const rects: { left: number; top: number; width: number; height: number }[] = []
  let offset = 0
  const lines = value.split("\n")
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const lineStart = offset
    const lineEnd = offset + line.length
    const s = Math.max(start, lineStart)
    const e = Math.min(end, lineEnd)
    if (s < e) {
      const pS = getTextareaSelectionPoint(textarea, s)
      const pE = getTextareaSelectionPoint(textarea, e)
      const ta = textarea.getBoundingClientRect()
      const sc = scrollEl.getBoundingClientRect()
      const left = ta.left - sc.left + scrollEl.scrollLeft + Math.min(pS.x, pE.x)
      const top = ta.top - sc.top + scrollEl.scrollTop + pS.y
      const width = Math.max(2, Math.abs(pE.x - pS.x))
      rects.push({ left, top, width, height: lh })
    }
    offset = lineEnd + 1
  }
  return rects
}

function getTextareaSelectionPoint(textarea: HTMLTextAreaElement, position: number) {
  const value = textarea.value ?? ""
  const clamped = Math.max(0, Math.min(position, value.length))

  const styles = window.getComputedStyle(textarea)
  const div = document.createElement("div")
  div.style.position = "absolute"
  div.style.visibility = "hidden"
  div.style.pointerEvents = "none"
  div.style.whiteSpace = "pre-wrap"
  div.style.wordWrap = "break-word"
  div.style.overflow = "hidden"
  div.style.top = "0"
  div.style.left = "-9999px"

  const propsToCopy = [
    "boxSizing",
    "width",
    "fontFamily",
    "fontSize",
    "fontWeight",
    "fontStyle",
    "letterSpacing",
    "lineHeight",
    "textTransform",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "borderTopWidth",
    "borderRightWidth",
    "borderBottomWidth",
    "borderLeftWidth",
  ] as const

  for (const prop of propsToCopy) {
    ; (div.style as any)[prop] = (styles as any)[prop]
  }

  div.textContent = value.slice(0, clamped)
  const span = document.createElement("span")
  span.textContent = "\u200b"
  div.appendChild(span)
  document.body.appendChild(div)

  const spanRect = span.getBoundingClientRect()
  const divRect = div.getBoundingClientRect()
  document.body.removeChild(div)

  const x = spanRect.left - divRect.left - textarea.scrollLeft
  const y = spanRect.top - divRect.top - textarea.scrollTop
  return { x, y }
}

interface Query {
  id: string
  name: string
  sql: string
  isFavorite?: boolean
  savedId?: string
}

const MAX_SQL_HISTORY = 80

type SqlHistoryStacks = { undo: string[]; redo: string[] }

function pushSqlUndo(stacks: Record<string, SqlHistoryStacks>, tabId: string, previousSql: string, nextSql: string) {
  if (previousSql === nextSql) return
  const h = stacks[tabId] ?? { undo: [], redo: [] }
  h.undo.push(previousSql)
  if (h.undo.length > MAX_SQL_HISTORY) {
    h.undo.shift()
  }
  h.redo = []
  stacks[tabId] = h
}

export default function QueryEditor() {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [queries, setQueries] = useState<Query[]>([])
  const [activeQueryId, setActiveQueryId] = useState("")
  const activeQuery = useMemo(() => queries.find((q) => q.id === activeQueryId), [queries, activeQueryId])
  const [activeSql, setActiveSql] = useState("")
  const activeSqlRef = useRef("")
  const [activeTab, setActiveTab] = useState<"table" | "chart" | "number">("table")
  const [selectedResource, setSelectedResource] = useState<string>("")
  const [queryAssistantOpen, setQueryAssistantOpen] = useState(false)
  const [queryQuestion, setQueryQuestion] = useState("")
  const [selectionAssistantOpen, setSelectionAssistantOpen] = useState(false)
  const [selectionQuestion, setSelectionQuestion] = useState("")
  const [sqlSelection, setSqlSelection] = useState<{ start: number; end: number; text: string } | null>(null)
  const [frozenSqlSelection, setFrozenSqlSelection] = useState<{ start: number; end: number; text: string } | null>(
    null
  )
  const [selectionHighlightRects, setSelectionHighlightRects] = useState<
    { left: number; top: number; width: number; height: number }[]
  >([])
  const [selectionBubble, setSelectionBubble] = useState<{ x: number; y: number } | null>(null)
  const sqlEditorRef = useRef<HTMLDivElement>(null)
  const sqlEditorScrollRef = useRef<HTMLDivElement>(null)
  const lastSqlSelectionRef = useRef<{ start: number; end: number; text: string } | null>(null)
  const selectionRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const queryQuestionInputRef = useRef<{
    focus: () => void
    setValue: (value: string) => void
    getValue: () => string
  } | null>(null)
  const selectionQuestionInputRef = useRef<{
    focus: () => void
    setValue: (value: string) => void
    getValue: () => string
  } | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [expandedSections, setExpandedSections] = useState({
    favorites: true,
    private: true,
  })
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  const saveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const sqlHistoryRef = useRef<Record<string, SqlHistoryStacks>>({})
  const skipSqlHistoryRef = useRef(false)
  const activeQueryIdRef = useRef("")
  const highlightedSqlCacheRef = useRef<{ code: string; html: string }>({ code: "", html: "" })
  const autosaveInFlightRef = useRef<Record<string, boolean>>({})
  const runForTabIdRef = useRef<string | null>(null)
  const runContextRef = useRef<{ tabId: string; sql: string; resource: string; isFavorite: boolean } | null>(null)
  const renameContextRef = useRef<{ savedId: string; newName: string } | null>(null)
  const duplicateContextRef = useRef<{ sql: string; default_resource_id: string | null } | null>(null)
  const toggleContextRef = useRef<{ savedId: string; isFavorite: boolean } | null>(null)
  const consumedOpenSqlRef = useRef<string | null>(null)
  const consumedWidgetRef = useRef<string | null>(null)
  const pendingWidgetDataRef = useRef<{ queryId: string; data: any } | null>(null)
  const widgetConfigRef = useRef<{ chartConfig?: any; numberConfig?: any } | null>(null)
  const widgetQueryIdRef = useRef<string | null>(null)

  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [renameQuery, setRenameQuery] = useState<SavedQueryItem | null>(null)
  const [renameValue, setRenameValue] = useState("")
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [queryToDelete, setQueryToDelete] = useState<SavedQueryItem | null>(null)
  const [queryResults, setQueryResults] = useState<Record<string, Record<string, unknown>[]>>({})
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [chartConfig, setChartConfig] = useState<{ chartType: "bar" | "line" | "pie"; xAxis: string; yAxis: string }>({
    chartType: "bar",
    xAxis: "",
    yAxis: "",
  })
  const [numberConfig, setNumberConfig] = useState<{
    column: string
    label?: string
    fontSize?: number
    color?: string
    labelColor?: string
  }>({
    column: "",
  })
  const [editingWidget, setEditingWidget] = useState<{
    boardId: string
    widgetId: string
    name: string
  } | null>(null)

  const { data: resourcesData } = useListResources()
  const resources = useMemo(() => resourcesData ?? [], [resourcesData])

  useEffect(() => {
    if (selectedResource) return
    const first = resources[0]
    if (!first) return
    setSelectedResource(first.id)
  }, [resources, selectedResource])

  const { data: containersData } = useListContainers(selectedResource || "")
  const containers = containersData ?? []
  const firstContainer = containers[0]
  const { data: containerFieldsData } = useListContainerFields(selectedResource || "", firstContainer?.id || "")
  const containerFields = containerFieldsData ?? []
  const { data: savedList = [], isLoading: savedListLoading } = useListSavedQueries()
  const { toast } = useToast()

  useEffect(() => {
    const next = activeQuery?.sql ?? ""
    setActiveSql(next)
    activeSqlRef.current = next
  }, [activeQueryId, activeQuery?.sql])

  const commitSqlToTab = useCallback((tabId: string, sql: string) => {
    setQueries((prev) => {
      const cur = prev.find((q) => q.id === tabId)
      if (!cur || cur.sql === sql) return prev
      return prev.map((q) => (q.id === tabId ? { ...q, sql } : q))
    })
  }, [])

  const deferredSql = useDeferredValue(activeSql)
  useEffect(() => {
    try {
      highlightedSqlCacheRef.current = {
        code: deferredSql,
        html: Prism.highlight(deferredSql, Prism.languages.sql, "sql"),
      }
    } catch {
      highlightedSqlCacheRef.current = { code: deferredSql, html: deferredSql }
    }
  }, [deferredSql])

  const autosaveSql = useCallback(
    async (tabId: string, sql: string) => {
      if (!selectedResource) return
      if (widgetQueryIdRef.current === tabId) return
      const trimmed = sql.trim()
      if (!trimmed) return

      if (autosaveInFlightRef.current[tabId]) return
      autosaveInFlightRef.current[tabId] = true
      try {
        const tab = queries.find((q) => q.id === tabId)
        if (!tab) return

        if (tab.savedId) {
          await saveQueryHttp({
            id: tab.savedId,
            sql: trimmed,
            default_resource_id: selectedResource,
            is_favorite: tab.isFavorite ?? false,
            name: tab.name,
          })

          // Avoid invalidating saved-queries on every keystroke; update cache in-place.
          queryClient.setQueryData(["editor", "saved-queries"], (prev: SavedQueryItem[] | undefined) => {
            if (!prev) return prev
            return prev.map((q) => (q.id === tab.savedId ? { ...q, sql: trimmed } : q))
          })
          return
        }

        // First edit of a new tab creates the saved query.
        const res = (await saveQueryHttp({
          name: tab.name,
          sql: trimmed,
          default_resource_id: selectedResource,
          is_favorite: tab.isFavorite ?? false,
        })) as { id?: string; name?: string }

        if (!res?.id) return

        setQueries((prev) =>
          prev.map((q) => (q.id === tabId ? { ...q, savedId: res.id, name: res.name ?? q.name } : q))
        )
        queryClient.setQueryData(["editor", "saved-queries"], (prev: SavedQueryItem[] | undefined) => {
          const next: SavedQueryItem = {
            id: res.id as string,
            name: res.name ?? tab.name,
            sql: trimmed,
            default_resource_id: selectedResource,
            is_favorite: tab.isFavorite ?? false,
          }
          if (!prev) return [next]
          if (prev.some((q) => q.id === next.id)) return prev
          return [next, ...prev]
        })
      } catch {
        // Silent: autosave shouldn't toast on every failure while typing.
      } finally {
        autosaveInFlightRef.current[tabId] = false
      }
    },
    [queryClient, queries, selectedResource]
  )

  const refreshSelectionChrome = useCallback(() => {
    const root = sqlEditorRef.current
    const scrollEl = sqlEditorScrollRef.current
    if (!root || !scrollEl) return
    const textarea = root.querySelector("textarea") as HTMLTextAreaElement | null
    if (!textarea) return

    const range = selectionAssistantOpen && frozenSqlSelection ? frozenSqlSelection : null

    if (range) {
      const caret = getTextareaSelectionPoint(textarea, range.end)
      const taRect = textarea.getBoundingClientRect()
      const rootRect = root.getBoundingClientRect()
      const x = taRect.left - rootRect.left + caret.x
      const y = taRect.top - rootRect.top + caret.y
      setSelectionBubble({ x, y })
      setSelectionHighlightRects(getSqlHighlightRects(textarea, scrollEl, range.start, range.end))
      return
    }

    const start = textarea.selectionStart ?? 0
    const end = textarea.selectionEnd ?? 0
    if (start === end) {
      setSqlSelection(null)
      setSelectionBubble(null)
      setSelectionHighlightRects([])
      return
    }

    const text = (textarea.value ?? "").slice(start, end)
    const sel = { start, end, text }
    setSqlSelection(sel)
    lastSqlSelectionRef.current = sel

    const caret = getTextareaSelectionPoint(textarea, end)
    const taRect = textarea.getBoundingClientRect()
    const rootRect = root.getBoundingClientRect()

    const x = taRect.left - rootRect.left + caret.x
    const y = taRect.top - rootRect.top + caret.y
    setSelectionBubble({ x, y })
    setSelectionHighlightRects([])
  }, [selectionAssistantOpen, frozenSqlSelection])

  const scheduleSelectionRefresh = useCallback(() => {
    // `refreshSelectionChrome` does DOM measurement; avoid doing it on every keypress.
    if (selectionRefreshTimerRef.current) clearTimeout(selectionRefreshTimerRef.current)
    selectionRefreshTimerRef.current = setTimeout(() => {
      requestAnimationFrame(() => refreshSelectionChrome())
      selectionRefreshTimerRef.current = null
    }, 80)
  }, [refreshSelectionChrome])

  const scheduleAutosave = useCallback(
    (tabId: string, sql: string) => {
      if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current)
      if (!sql.trim()) return
      saveDebounceRef.current = setTimeout(() => {
        void autosaveSql(tabId, sql)
        saveDebounceRef.current = null
      }, 2000)
    },
    [autosaveSql]
  )

  useEffect(() => {
    const scroller = sqlEditorScrollRef.current
    if (!scroller) return

    const onScroll = () => {
      requestAnimationFrame(() => refreshSelectionChrome())
    }
    scroller.addEventListener("scroll", onScroll, { passive: true })
    return () => scroller.removeEventListener("scroll", onScroll)
  }, [refreshSelectionChrome])

  const runQueryMutation = useRunQuery({
    onSuccess: (data) => {
      const tid = runForTabIdRef.current
      if (tid != null && Array.isArray(data)) {
        setQueryResults((prev) => ({ ...prev, [tid]: data as Record<string, unknown>[] }))
      }
      // Don't change the active tab - keep user on their current tab (chart/number/table)
      const ctx = runContextRef.current
      runContextRef.current = null
      if (ctx) {
        void autosaveSql(ctx.tabId, ctx.sql)
      }
    },
    onError: (err) => {
      runContextRef.current = null
      toast({ description: parseApiError(err), variant: "destructive" })
    },
  })

  const generateQueryMutation = useGenerateQuery({
    onSuccess: ({ query }) => {
      if (!query) return
      const tabId = activeQueryIdRef.current
      const prevSql = activeSqlRef.current
      pushSqlUndo(sqlHistoryRef.current, tabId, prevSql, query)
      setActiveSql(query)
      activeSqlRef.current = query
      commitSqlToTab(tabId, query)
      setQueryQuestion("")
      setQueryAssistantOpen(false)
    },
    onError: (err) => {
      toast({ description: parseApiError(err), variant: "destructive" })
    },
  })

  const updateQueryMutation = useUpdateQuery({
    onSuccess: ({ query }) => {
      if (!query) return
      const tabId = activeQueryIdRef.current
      const prevSql = activeSqlRef.current
      pushSqlUndo(sqlHistoryRef.current, tabId, prevSql, query)
      setActiveSql(query)
      activeSqlRef.current = query
      commitSqlToTab(tabId, query)
      setSelectionQuestion("")
      setSelectionAssistantOpen(false)
      setFrozenSqlSelection(null)
      setSqlSelection(null)
      setSelectionBubble(null)
      setSelectionHighlightRects([])
    },
    onError: (err) => {
      toast({ description: parseApiError(err), variant: "destructive" })
    },
  })
  const saveQueryMutation = useSaveQuery({
    onSuccess: (res: { id?: string; name?: string }) => {
      queryClient.invalidateQueries({ queryKey: ["editor", "saved-queries"] })
      if (res?.id && duplicateContextRef.current) {
        const ctx = duplicateContextRef.current
        duplicateContextRef.current = null
        const tab: Query = {
          id: crypto.randomUUID(),
          name: res.name ?? "Copy",
          sql: ctx.sql,
          isFavorite: false,
          savedId: res.id,
        }
        setQueries((prev) => [...prev, tab])
        setActiveQueryId(tab.id)
        toast({ description: "Query duplicated." })
      }
      if (renameContextRef.current) {
        const { savedId, newName } = renameContextRef.current
        renameContextRef.current = null
        setRenameDialogOpen(false)
        setRenameQuery(null)
        setQueries((prev) => prev.map((q) => (q.savedId === savedId ? { ...q, name: newName } : q)))
        toast({ description: "Query renamed." })
      }
      if (toggleContextRef.current) {
        const { savedId, isFavorite } = toggleContextRef.current
        toggleContextRef.current = null
        setQueries((prev) => prev.map((q) => (q.savedId === savedId ? { ...q, isFavorite } : q)))
        toast({ description: isFavorite ? "Added to favorites." : "Removed from favorites." })
      }
    },
    onError: (err) => {
      renameContextRef.current = null
      duplicateContextRef.current = null
      toggleContextRef.current = null
      toast({ description: parseApiError(err), variant: "destructive" })
    },
  })
  const updateWidgetMutation = useUpdateWidget()
  const deleteQueryMutation = useDeleteQuery({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["editor", "saved-queries"] })
      const uid = queryToDelete?.id
      setDeleteDialogOpen(false)
      setQueryToDelete(null)
      if (uid) {
        const tab = queries.find((t) => t.savedId === uid)
        if (tab) handleCloseQuery({ stopPropagation: () => { } } as React.MouseEvent, tab.id)
      }
      toast({ description: "Query deleted." })
    },
    onError: (err) => toast({ description: parseApiError(err), variant: "destructive" }),
  })

  activeQueryIdRef.current = activeQueryId
  const stacks = sqlHistoryRef.current[activeQueryId]
  void stacks
  const activeResult = queryResults[activeQueryId]
  const isRunningForActive = runQueryMutation.isPending && runForTabIdRef.current === activeQueryId
  const runErrorForActive = runQueryMutation.isError && runForTabIdRef.current === activeQueryId

  const isEditingWidget =
    editingWidget != null && widgetQueryIdRef.current != null && activeQueryId === widgetQueryIdRef.current

  useLayoutEffect(() => {
    if (!selectionAssistantOpen || !frozenSqlSelection) return
    requestAnimationFrame(() => refreshSelectionChrome())
  }, [selectionAssistantOpen, frozenSqlSelection, activeSql, refreshSelectionChrome])

  const filtered = useMemo(
    () => (savedList || []).filter((q) => !searchQuery || q.name.toLowerCase().includes(searchQuery.toLowerCase())),
    [savedList, searchQuery]
  )
  const favoriteQueries = useMemo(() => filtered.filter((q) => q.is_favorite), [filtered])
  const privateQueries = useMemo(() => filtered.filter((q) => !q.is_favorite), [filtered])

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const handleRunQuery = () => {
    if (!selectedResource) {
      toast({ description: "Select a resource to run the query.", variant: "destructive" })
      return
    }
    const sql = activeSql.trim()
    if (!sql) {
      toast({ description: "Enter a SQL query to run.", variant: "destructive" })
      return
    }
    // Don't save query if we're editing a widget
    const isWidgetQuery = widgetQueryIdRef.current === activeQueryId
    if (activeQuery && !activeQuery.savedId && !isWidgetQuery) {
      runContextRef.current = {
        tabId: activeQueryId,
        sql: activeSql,
        resource: selectedResource,
        isFavorite: activeQuery.isFavorite ?? false,
      }
    }
    runForTabIdRef.current = activeQueryId
    runQueryMutation.mutate({ resource_id: selectedResource, query: sql })
  }

  const handleSqlUndo = useCallback(() => {
    const tabId = activeQueryIdRef.current
    const h = sqlHistoryRef.current[tabId]
    if (!h?.undo.length) return
    const tab = queries.find((q) => q.id === tabId)
    if (!tab) return
    const currentSql = activeSqlRef.current
    const nextSql = h.undo.pop()!
    h.redo.push(currentSql)
    sqlHistoryRef.current[tabId] = h
    skipSqlHistoryRef.current = true
    setActiveSql(nextSql)
    activeSqlRef.current = nextSql
    commitSqlToTab(tabId, nextSql)
    if (tab.savedId) {
      if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current)
      saveDebounceRef.current = setTimeout(() => {
        void autosaveSql(tabId, nextSql)
        saveDebounceRef.current = null
      }, 2000)
    }
  }, [autosaveSql, commitSqlToTab, queries])

  const handleSqlRedo = useCallback(() => {
    const tabId = activeQueryIdRef.current
    const h = sqlHistoryRef.current[tabId]
    if (!h?.redo.length) return
    const tab = queries.find((q) => q.id === tabId)
    if (!tab) return
    const currentSql = activeSqlRef.current
    const nextSql = h.redo.pop()!
    h.undo.push(currentSql)
    sqlHistoryRef.current[tabId] = h
    skipSqlHistoryRef.current = true
    setActiveSql(nextSql)
    activeSqlRef.current = nextSql
    commitSqlToTab(tabId, nextSql)
    if (tab.savedId) {
      if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current)
      saveDebounceRef.current = setTimeout(() => {
        void autosaveSql(tabId, nextSql)
        saveDebounceRef.current = null
      }, 2000)
    }
  }, [autosaveSql, commitSqlToTab, queries])

  const handleCloseQuery = (e: React.MouseEvent, queryId: string) => {
    e.stopPropagation()
    if (queryId === activeQueryId) {
      commitSqlToTab(queryId, activeSqlRef.current)
    }
    // Clear widget query ref if closing the widget query
    if (widgetQueryIdRef.current === queryId) {
      widgetQueryIdRef.current = null
      widgetConfigRef.current = null
    }
    setQueryResults((prev) => {
      const next = { ...prev }
      delete next[queryId]
      return next
    })
    const remaining = queries.filter((q) => q.id !== queryId)
    if (remaining.length === 0) {
      const newQuery: Query = {
        id: crypto.randomUUID(),
        name: "New query",
        sql: "",
        isFavorite: false,
      }
      setQueries([newQuery])
      setActiveQueryId(newQuery.id)
    } else {
      setQueries(remaining)
      if (activeQueryId === queryId) {
        setActiveQueryId(remaining[0]?.id ?? "")
      }
    }
  }

  const handleNewQuery = () => {
    // Clear widget query ref when creating a new query
    widgetQueryIdRef.current = null
    widgetConfigRef.current = null
    const newQuery: Query = {
      id: crypto.randomUUID(),
      name: "New query",
      sql: "",
      isFavorite: false,
    }
    setQueries((prev) => [...prev, newQuery])
    setActiveQueryId(newQuery.id)
  }

  const handleSelectQueryTab = (nextId: string) => {
    if (nextId === activeQueryId) return
    if (activeQueryId) commitSqlToTab(activeQueryId, activeSqlRef.current)
    setActiveQueryId(nextId)
  }

  const handleOpenSavedQuery = (q: SavedQueryItem) => {
    const existing = queries.find((t) => t.savedId === q.id)
    if (existing) {
      setActiveQueryId(existing.id)
      return
    }
    const tab: Query = {
      id: crypto.randomUUID(),
      name: q.name,
      sql: q.sql,
      isFavorite: q.is_favorite,
      savedId: q.id,
    }
    setQueries((prev) => [...prev, tab])
    setActiveQueryId(tab.id)
    if (q.default_resource_id) setSelectedResource(q.default_resource_id)
  }

  const handleRenameClick = (q: SavedQueryItem) => {
    setRenameQuery(q)
    setRenameValue(q.name)
    setRenameDialogOpen(true)
  }

  const handleRenameSubmit = () => {
    if (!renameQuery || !renameValue.trim()) return
    renameContextRef.current = { savedId: renameQuery.id, newName: renameValue.trim() }
    saveQueryMutation.mutate({
      id: renameQuery.id,
      name: renameValue.trim(),
      sql: renameQuery.sql,
      default_resource_id: renameQuery.default_resource_id ?? undefined,
      is_favorite: renameQuery.is_favorite,
    })
  }

  const handleDuplicateClick = (q: SavedQueryItem) => {
    duplicateContextRef.current = {
      sql: q.sql,
      default_resource_id: q.default_resource_id,
    }
    saveQueryMutation.mutate({
      name: `Copy of ${q.name}`,
      sql: q.sql,
      default_resource_id: q.default_resource_id ?? undefined,
      is_favorite: false,
    })
  }

  const handleToggleFavoriteClick = (q: SavedQueryItem) => {
    toggleContextRef.current = { savedId: q.id, isFavorite: !q.is_favorite }
    saveQueryMutation.mutate({
      id: q.id,
      name: q.name,
      sql: q.sql,
      default_resource_id: q.default_resource_id ?? undefined,
      is_favorite: !q.is_favorite,
    })
  }

  const handleDeleteClick = (q: SavedQueryItem) => {
    setQueryToDelete(q)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (queryToDelete) deleteQueryMutation.mutate(queryToDelete.id)
  }

  useEffect(
    () => () => {
      if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current)
      if (selectionRefreshTimerRef.current) clearTimeout(selectionRefreshTimerRef.current)
    },
    []
  )

  useEffect(() => {
    const state = location.state as { openSql?: string; resourceId?: string; widget?: any } | null
    const hasOpenSql = typeof state?.openSql === "string" && state.openSql.length > 0
    const hasWidget = state?.widget != null
    if (hasOpenSql || hasWidget) return
    if (queries.length === 0) {
      const newQuery: Query = {
        id: crypto.randomUUID(),
        name: "New query",
        sql: "",
        isFavorite: false,
      }
      setQueries([newQuery])
      setActiveQueryId(newQuery.id)
    }
  }, [queries.length, location.state])

  // Handle widget data from navigation
  useEffect(() => {
    const state = location.state as {
      widget?: {
        board_id?: string
        widget_id?: string
        name?: string
        sql_query: string
        resource_id: string
        widget_type: string
        config?: any
        data?: any
      }
    } | null
    const widget = state?.widget
    if (!widget) return

    // Prevent re-processing the same widget
    const widgetKey = `${widget.sql_query}-${widget.resource_id}-${widget.widget_type}`
    if (widgetKey === consumedWidgetRef.current) {
      navigate(location.pathname, { replace: true, state: {} })
      return
    }
    consumedWidgetRef.current = widgetKey

    // Track when we're editing an existing widget (so we can show Save/Cancel)
    if (widget.board_id && widget.widget_id) {
      setEditingWidget({
        boardId: widget.board_id,
        widgetId: widget.widget_id,
        name: widget.name ?? "Widget",
      })
    }

    // Set resource first
    if (widget.resource_id) {
      setSelectedResource(widget.resource_id)
    }

    // Create query with widget SQL
    const newQueryId = crypto.randomUUID()
    const newQuery: Query = {
      id: newQueryId,
      name: "Widget Query",
      sql: widget.sql_query || "",
      isFavorite: false,
    }

    // Set widget type tab before adding query
    if (widget.widget_type === "chart") {
      setActiveTab("chart")
      // Set chart config if available
      if (widget.config) {
        // For pie charts, use category/value; for others, use xAxis/yAxis
        const xAxis = widget.config.xAxis || widget.config.category || ""
        const yAxis = widget.config.yAxis || widget.config.value || ""
        const chartConfigValue = {
          chartType: widget.config.chartType || "bar",
          xAxis,
          yAxis,
        }
        setChartConfig(chartConfigValue)
        widgetConfigRef.current = { chartConfig: chartConfigValue }
      }
    } else if (widget.widget_type === "number") {
      setActiveTab("number")
      // Set number config if available
      if (widget.config) {
        // Widget config uses 'valueKey' but NumberTab uses 'column'
        const numberConfigValue = {
          column: widget.config.valueKey || widget.config.column || "",
          label: widget.config.label,
          fontSize: widget.config.fontSize,
          color: widget.config.color,
          labelColor: widget.config.labelColor,
        }
        setNumberConfig(numberConfigValue)
        widgetConfigRef.current = { numberConfig: numberConfigValue }
      }
    } else {
      setActiveTab("table")
      widgetConfigRef.current = null
    }

    // Add query and set active query ID
    setQueries((prev) => [...prev, newQuery])
    setActiveQueryId(newQueryId)
    // Track this as a widget query to prevent saving
    widgetQueryIdRef.current = newQueryId

    // If widget data is provided, set results immediately so chart/table/number pre-render on the same commit
    if (widget.data && Array.isArray(widget.data)) {
      setQueryResults((prev) => ({ ...prev, [newQueryId]: widget.data }))
      navigate(location.pathname, { replace: true, state: {} })
      return
    }

    // Otherwise, run the query automatically
    if (widget.sql_query && widget.resource_id) {
      runForTabIdRef.current = newQueryId
      runQueryMutation.mutate({ resource_id: widget.resource_id, query: widget.sql_query })
    }

    navigate(location.pathname, { replace: true, state: {} })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.pathname, navigate])

  // Set pending widget data after query is added
  useEffect(() => {
    const pendingData = pendingWidgetDataRef.current
    if (pendingData && activeQueryId === pendingData.queryId) {
      setQueryResults((prev) => ({ ...prev, [pendingData.queryId]: pendingData.data }))
      pendingWidgetDataRef.current = null
    }
  }, [activeQueryId, queries])

  // Clear widget config ref when switching queries (unless it's the widget query)
  useEffect(() => {
    const pendingData = pendingWidgetDataRef.current
    if (!pendingData && widgetConfigRef.current) {
      // Clear widget config after a delay to allow components to initialize
      const timer = setTimeout(() => {
        widgetConfigRef.current = null
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [activeQueryId])

  useEffect(() => {
    const state = location.state as { openSql?: string; resourceId?: string } | null
    const sql = typeof state?.openSql === "string" && state.openSql.length > 0 ? state.openSql : null
    const resourceId = typeof state?.resourceId === "string" && state.resourceId.length > 0 ? state.resourceId : null
    if (!sql) {
      if (resourceId) setSelectedResource(resourceId)
      if (resourceId) navigate(location.pathname, { replace: true, state: {} })
      return
    }
    if (sql === consumedOpenSqlRef.current) {
      if (resourceId) setSelectedResource(resourceId)
      navigate(location.pathname, { replace: true, state: {} })
      return
    }
    consumedOpenSqlRef.current = sql
    if (resourceId) setSelectedResource(resourceId)
    const newQuery: Query = {
      id: crypto.randomUUID(),
      name: "From Chat",
      sql,
      isFavorite: false,
    }
    setQueries((prev) => [...prev, newQuery])
    setActiveQueryId(newQuery.id)
    navigate(location.pathname, { replace: true, state: {} })
  }, [location.state, location.pathname, navigate])

  // Auto-collapse sidebar on tablet and mobile
  useEffect(() => {
    const checkScreenSize = () => {
      // Tablet: 768px and below, Mobile: 640px and below
      const isMobileOrTablet = window.innerWidth <= 768
      setSidebarCollapsed(isMobileOrTablet)
    }

    checkScreenSize()
    window.addEventListener("resize", checkScreenSize)
    return () => window.removeEventListener("resize", checkScreenSize)
  }, [])

  const handleSaveWidget = async () => {
    if (!editingWidget || !activeSql.trim() || !selectedResource) {
      toast({ description: "SQL and resource are required to save.", variant: "destructive" })
      return
    }
    try {
      let config: Record<string, any> = {}
      if (activeTab === "chart") {
        config = { chartType: chartConfig.chartType, xAxis: chartConfig.xAxis, yAxis: chartConfig.yAxis }
      } else if (activeTab === "number") {
        config = {
          valueKey: numberConfig.column,
          column: numberConfig.column,
          label: numberConfig.label,
          fontSize: numberConfig.fontSize,
          color: numberConfig.color,
          labelColor: numberConfig.labelColor,
        }
      }
      await updateWidgetMutation.mutateAsync({
        boardId: editingWidget.boardId,
        widgetId: editingWidget.widgetId,
        sql_query: activeSql.trim(),
        resource_id: selectedResource,
        widget_type: activeTab,
        config: Object.keys(config).length > 0 ? config : undefined,
      })
      toast({ description: "Widget updated." })
      setEditingWidget(null)
      queryClient.invalidateQueries({
        queryKey: ["boards", editingWidget.boardId, "widgets", editingWidget.widgetId, "data"],
      })
      navigate(ROUTES.DASHBOARD.BOARDS.DETAIL(editingWidget.boardId))
    } catch (err) {
      toast({ description: parseApiError(err as Parameters<typeof parseApiError>[0]), variant: "destructive" })
    }
  }

  const handleCancelEditWidget = () => {
    if (!editingWidget) return
    setEditingWidget(null)
    navigate(ROUTES.DASHBOARD.BOARDS.DETAIL(editingWidget.boardId))
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <SavedQueriesSidebar
        sidebarCollapsed={sidebarCollapsed}
        onBack={() => navigate(-1)}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onNewQuery={handleNewQuery}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
        favoriteQueries={favoriteQueries}
        privateQueries={privateQueries}
        savedListLoading={savedListLoading}
        activeSavedId={activeQuery?.savedId}
        onOpenSavedQuery={handleOpenSavedQuery}
        onRename={handleRenameClick}
        onDuplicate={handleDuplicateClick}
        onToggleFavorite={handleToggleFavoriteClick}
        onDelete={handleDeleteClick}
      />

      {/* Main Content Area */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* Query Tabs */}
        <div className="my-2 shrink-0 border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-[#0F0F12]">
          <div className="flex items-center gap-1 overflow-x-auto px-3">
            {/* Sidebar Toggle Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="h-7 w-7 shrink-0"
              tooltip={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {sidebarCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </Button>
            {queries.map((query) => (
              <button
                key={query.id}
                onClick={() => handleSelectQueryTab(query.id)}
                className={`group flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2.5 py-1.5 text-xs font-medium transition-colors ${activeQueryId === query.id
                    ? "border-gray-900 text-gray-900 dark:border-white dark:text-white"
                    : "border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                  }`}
              >
                <span className="min-w-0 truncate">{query.name}</span>
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={(e) => handleCloseQuery(e, query.id)}
                  className="shrink-0 rounded p-0.5 opacity-0 transition-opacity hover:bg-gray-200 group-hover:opacity-100 dark:hover:bg-gray-700"
                  aria-label="Close query"
                >
                  <X className="h-3 w-3" />
                </span>
              </button>
            ))}
            <button
              onClick={handleNewQuery}
              className="flex items-center gap-1.5 whitespace-nowrap border-b-2 border-transparent px-2.5 py-1.5 text-xs font-medium text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
              aria-label="New query"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Resizable Panels: Editor and Results */}
        <PanelGroup direction="vertical" className="min-h-0 flex-1">
          {/* SQL Editor Panel */}
          <Panel defaultSize={40} minSize={20}>
            <SqlEditorPanel
              activeQueryId={activeQueryId}
              activeQuerySqlForUpdate={activeSql}
              activeSql={activeSql}
              setActiveSql={setActiveSql}
              activeSqlRef={activeSqlRef}
              sqlEditorRef={sqlEditorRef}
              sqlEditorScrollRef={sqlEditorScrollRef}
              lastSqlSelectionRef={lastSqlSelectionRef}
              skipSqlHistoryRef={skipSqlHistoryRef}
              sqlHistoryRef={sqlHistoryRef}
              pushSqlUndo={(tabId, previousSql, nextSql) =>
                pushSqlUndo(sqlHistoryRef.current, tabId, previousSql, nextSql)
              }
              selectedResource={selectedResource}
              setSelectedResource={setSelectedResource}
              resources={resources}
              queryAssistantOpen={queryAssistantOpen}
              setQueryAssistantOpen={setQueryAssistantOpen}
              queryQuestion={queryQuestion}
              setQueryQuestion={setQueryQuestion}
              queryQuestionInputRef={queryQuestionInputRef}
              selectionAssistantOpen={selectionAssistantOpen}
              setSelectionAssistantOpen={setSelectionAssistantOpen}
              selectionQuestion={selectionQuestion}
              setSelectionQuestion={setSelectionQuestion}
              selectionQuestionInputRef={selectionQuestionInputRef}
              frozenSqlSelection={frozenSqlSelection}
              setFrozenSqlSelection={setFrozenSqlSelection}
              sqlSelection={sqlSelection}
              setSqlSelection={setSqlSelection}
              setSelectionHighlightRects={setSelectionHighlightRects}
              selectionHighlightRects={selectionHighlightRects}
              selectionBubble={selectionBubble}
              refreshSelectionChrome={refreshSelectionChrome}
              scheduleSelectionRefresh={scheduleSelectionRefresh}
              highlightedSqlCacheRef={highlightedSqlCacheRef}
              autosaveAfterChange={scheduleAutosave}
              isWidgetQuery={widgetQueryIdRef.current === activeQueryId}
              generateQueryIsPending={generateQueryMutation.isPending}
              onGenerateQuery={(resourceId, question) =>
                generateQueryMutation.mutate({ resource_id: resourceId, question })
              }
              updateQueryIsPending={updateQueryMutation.isPending}
              onUpdateQuery={(args) => updateQueryMutation.mutate(args)}
              onRunQuery={handleRunQuery}
              onUndo={handleSqlUndo}
              onRedo={handleSqlRedo}
            />
          </Panel>

          {/* Resize Handle */}
          <PanelResizeHandle className="group relative flex w-full items-center justify-center border-t border-gray-200 bg-white dark:border-gray-800 dark:bg-[#0F0F12]">
            <div className="absolute inset-x-0 top-0 h-1 bg-transparent transition-colors group-hover:bg-gray-200 dark:group-hover:bg-gray-700" />
            <div className="absolute inset-x-0 top-0 flex h-1 items-center justify-center">
              <div className="h-0.5 w-8 rounded-full bg-gray-300 group-hover:bg-gray-900 dark:bg-gray-600 dark:group-hover:bg-gray-300" />
            </div>
          </PanelResizeHandle>

          {/* Results Panel */}
          <Panel defaultSize={60} minSize={20}>
            <QueryResultsPanel
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              selectedResource={selectedResource}
              onSelectResource={setSelectedResource}
              onAddResource={() => navigate(ROUTES.DASHBOARD.RESOURCES.CREATE)}
              resources={resources}
              activeQueryId={activeQueryId}
              activeSql={activeSql}
              activeResult={activeResult}
              containerFields={containerFields}
              isRunning={isRunningForActive}
              runError={runErrorForActive}
              runErrorValue={runQueryMutation.error}
              isRunPending={runQueryMutation.isPending}
              onRunQuery={handleRunQuery}
              onExport={() => setExportDialogOpen(true)}
              canExport={!!(activeSql.trim() && selectedResource && activeResult)}
              chartConfig={chartConfig}
              setChartConfig={setChartConfig}
              numberConfig={numberConfig}
              setNumberConfig={setNumberConfig}
              widgetConfigRef={widgetConfigRef}
            />
          </Panel>
        </PanelGroup>

        {/* Floating Save/Cancel when editing a widget */}
        {isEditingWidget && (
          <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2 transform">
            <div className="flex items-center gap-2.5 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800">
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Widget changes</span>
              <div className="h-4 w-px bg-gray-200 dark:bg-gray-700" />
              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelEditWidget}
                  className="h-7 gap-1.5 px-2.5 text-xs"
                >
                  <X className="h-3 w-3" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveWidget}
                  disabled={updateWidgetMutation.isPending || !activeSql.trim() || !selectedResource}
                  className="h-7 gap-1.5 px-2.5 text-xs"
                >
                  {updateWidgetMutation.isPending ? (
                    <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : (
                    <Save className="h-3 w-3" />
                  )}
                  {updateWidgetMutation.isPending ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Save new query dialog */}
      {/* (removed) Save dialog: editor auto-saves on changes */}

      {/* Rename dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename query</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rename-query-name">Name</Label>
              <Input
                id="rename-query-name"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                placeholder="Query name"
                className="text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRenameSubmit} disabled={saveQueryMutation.isPending || !renameValue.trim()}>
              {saveQueryMutation.isPending ? (
                <span className="mr-1.5 h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-xl overflow-visible p-0">
          <div className="flex items-start justify-between px-6 pt-6">
            <div className="rounded-lg border border-gray-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900">
              <Trash2 className="h-6 w-6 text-red-500" />
            </div>
          </div>
          <DialogHeader className="px-6 pt-1">
            <DialogTitle className="text-2xl font-semibold">Delete query</DialogTitle>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Are you sure you want to delete &ldquo;{queryToDelete?.name}&rdquo;? This action cannot be undone.
            </p>
          </DialogHeader>
          <div className="flex items-center justify-end gap-6 px-6 pb-5 pt-2">
            <Button variant="ghost" onClick={() => setDeleteDialogOpen(false)} disabled={deleteQueryMutation.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleteQueryMutation.isPending}>
              {deleteQueryMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ExportToBoardDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        widgetType={
          activeTab === "chart"
            ? "chart"
            : activeTab === "number"
              ? "number"
              : activeTab === "table"
                ? "table"
                : "table"
        }
        sqlQuery={activeSql}
        resourceId={selectedResource}
        data={activeResult}
        chartConfig={activeTab === "chart" ? chartConfig : undefined}
        numberConfig={activeTab === "number" ? numberConfig : undefined}
      />
    </div>
  )
}
